import { computed, ComputedRef } from 'vue';
import { useStore } from 'vuex';
import { AppState } from '@/store';
import { UserRole } from '@/types/UserRole';
import { type UserPermission } from '@/types/UserPermissions';

export interface UserComposable {
  sub: ComputedRef<string>;
  email: ComputedRef<string>;
  name: ComputedRef<string>;
  phone: ComputedRef<string>;
  identity: ComputedRef<keyof typeof UserRole>;
  tenantName: ComputedRef<string>;
  role: ComputedRef<UserRole>;
  permissions: ComputedRef<string[]>;
  allRouteGroupsAllowed: ComputedRef<boolean>;
  allowedRouteGroups: ComputedRef<string[]>;
  allAccountsAllowed: ComputedRef<boolean>;
  allowedCarrierIds: ComputedRef<number[]>;
  allowedWholesalerIds: ComputedRef<number[]>;
  mfaEnabled: ComputedRef<boolean>;
  legalDisclaimerAccepted: ComputedRef<boolean>;
  clientId: ComputedRef<string>;
  correlationId: ComputedRef<string>;
  isExternalClient: ComputedRef<boolean>;
  isLoggedIn: ComputedRef<boolean>;
  hasPermissions: (...permissions: UserPermission[]) => boolean;
}

export function useUser(): UserComposable {
  const store = useStore<AppState>();

  const user = computed(() => store.state.user);
  const isClientViewEnabled = computed(() => store.state.clientViewState.isClientViewEnabled);
  // const activeClientView = computed(() => store.state.clientViewState.activeView);

  /**
   * Get the user's effective role considering client view override
   */
  const role = computed((): UserRole => {
    if (!user.value) {
      return UserRole.None;
    }

    // Override to client role if client view is enabled
    if (isClientViewEnabled.value) {
      return UserRole.Client;
    }

    // Otherwise return actual user role
    const roles = user.value.roles;
    if (!roles) {
      return UserRole.None;
    }

    const roleHierarchy = [
      UserRole.Admin,
      UserRole.Broker,
      UserRole.Carrier,
      UserRole.Client,
      UserRole.Market,
    ];

    for (const role of roleHierarchy) {
      if (roles.some((r) => r && r.endsWith(role))) {
        return role;
      }
    }

    return UserRole.None;
  });

  const sub = computed(() => user.value?.sub || '');

  const email = computed(() => user.value?.email || '');

  const name = computed(() => user.value?.name || '');

  const phone = computed(() => user.value?.phone || '');

  const identity = computed<keyof typeof UserRole>(
    () => (user.value?.identity || UserRole.None) as keyof typeof UserRole,
  );

  const tenantName = computed(() => user.value?.tenantName.trim() || '');

  const clientId = computed(() => user.value?.clientId || '');

  const correlationId = computed(() => user.value?.correlationId || '');

  const permissions = computed(() => {
    if (isClientViewEnabled.value) {
      return store.state.clientViewState.permissions;
    }
    return user.value?.permissions || [];
  });

  const allRouteGroupsAllowed = computed(() => {
    if (isClientViewEnabled.value) return false;
    return user.value?.allRouteGroupsAllowed || false;
  });

  const allowedRouteGroups = computed(() => {
    if (isClientViewEnabled.value) {
      return ['client', 'client-view'];
    }
    return user.value?.allowedRouteGroups || [];
  });

  const allAccountsAllowed = computed(() => {
    if (isClientViewEnabled.value) return false;
    return user.value?.allAccountsAllowed || false;
  });

  const allowedCarrierIds = computed(() => {
    // In client view mode, no carrier access
    if (isClientViewEnabled.value) return [];
    return user.value?.allowedCarrierIds || [];
  });

  const allowedWholesalerIds = computed(() => {
    // In client view mode, no wholesaler access
    if (isClientViewEnabled.value) return [];
    return user.value?.allowedWholesalerIds || [];
  });

  const mfaEnabled = computed(() => user.value?.mfaEnabled || false);

  const legalDisclaimerAccepted = computed(() => user.value?.legalDisclaimerAccepted || false);

  const isExternalClient = computed(() => {
    // In client view mode, always true
    if (isClientViewEnabled.value) return true;
    return user.value?.isExternalClient || false;
  });

  const isLoggedIn = computed(() => !!user.value);

  // ------------------
  // helper functions
  // ------------------
  function hasPermissions(...permissionsToCheck: UserPermission[]): boolean {
    if (role.value === UserRole.Admin) {
      return true;
    }
    const perms = permissions.value;
    if (!perms) {
      return false;
    }
    for (const toCheck of permissionsToCheck) {
      let found = false;
      for (const userPerm of perms) {
        if (userPerm && userPerm === toCheck) {
          found = true;
          break;
        }
      }
      if (!found) {
        return false;
      }
    }
    return true;
  }

  const userComposable = {
    sub,
    email,
    name,
    phone,
    identity,
    tenantName,
    role,
    permissions,
    allRouteGroupsAllowed,
    allowedRouteGroups,
    allAccountsAllowed,
    allowedCarrierIds,
    allowedWholesalerIds,
    mfaEnabled,
    legalDisclaimerAccepted,
    clientId,
    correlationId,
    isExternalClient,
    isLoggedIn,
    hasPermissions,
  } satisfies UserComposable;

  return userComposable;
}
